import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { StudentClass } from '@/types/class'
import { requiredString, studentClass } from '@/utils/zod'

const responseSchema = z.object({
  id: requiredString(),
  name: requiredString(),
  students: z.array(
    z.object({
      id: requiredString(),
      name: requiredString(),
      first_name: requiredString(),
      last_name: requiredString(),
      student_class: studentClass.nullable(),
      attendance_percentage: z.number(),
      behaviour_grade: z.string().nullable(),
      proposed_grade: z.string().nullable(),
      final_grade: z.string().nullable(),
      grades: z.array(z.string())
    })
  )
})

export type CourseGroupStudent = {
  id: string
  name: string
  studentClass?: StudentClass
  attendancePercentage: number
  behaviourGrade: string | null
  proposedGrade: string | null
  finalGrade: string | null
  grades: string[]
}

type CourseGroup = {
  id: string
  name: string
  students: CourseGroupStudent[]
}

const parseResponse = (
  response: z.infer<typeof responseSchema>
): CourseGroup => ({
  id: response.id,
  name: response.name,
  students: response.students.map(student => ({
    id: student.id,
    name: student.name,
    studentClass: student.student_class
      ? {
          id: student.student_class.id,
          name: student.student_class.display_name
        }
      : undefined,
    attendancePercentage: student.attendance_percentage,
    behaviourGrade: student.behaviour_grade,
    proposedGrade: student.proposed_grade,
    finalGrade: student.final_grade,
    grades: student.grades
  }))
})

const useCourseGroup = (id: string, semesterId?: string) =>
  useQuery({
    queryKey: ['panelLessonsCourseGroupsRetrieve', id, semesterId],
    staleTime: 60 * 100,
    queryFn: async () => {
      const groupResponse = await httpService.panel.panelLessonsGroupsRetrieve(
        id,
        {
          semester_id: semesterId,
          fetchKeys: {
            id: true,
            name: true,
            students: true
          }
        }
      )

      const group = parseResponse(responseSchema.parse(groupResponse))

      return group
    }
  })

export default useCourseGroup
